class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    facetForm.addEventListener('keydown', (event) => {
      // Support Enter key natively without double-toggling from screen readers
      if (event.key === 'Enter' && event.target && event.target.tagName === 'INPUT' && event.target.type === 'checkbox') {
        event.preventDefault(); // Stop native form submit
        
        const checkbox = event.target;
        const initialState = checkbox.checked;
        
        // Wait 10ms to see if UserWay/Screen Reader injected a synthetic click
        setTimeout(() => {
          // If the state hasn't changed, the screen reader didn't click it, so we do it manually
          if (checkbox.checked === initialState) {
            checkbox.click();
          }
        }, 10);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, shouldSetFocus = false, filterName = '') {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const activeElementId = document.activeElement ? document.activeElement.id : null;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section, index) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      // Only pass the announcement message down for checkbox toggles, not filter removals.
      // And only pass it for the first section to prevent double-announcements.
      const messageToPassDown = (!shouldSetFocus && index === 0) ? filterName : '';

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, activeElementId, messageToPassDown)
        : FacetFiltersForm.renderSectionFromFetch(url, event, activeElementId, messageToPassDown);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    
    // Set focus to filtered results area only when removing filters
    if (shouldSetFocus) {
      FacetFiltersForm.setFocusAfterFilterChange(filterName);
    }
  }

  static renderSectionFromFetch(url, event, activeElementId, announcementMessage = '') {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        
        if (activeElementId) {
          const elementToFocus = document.getElementById(activeElementId);
          if (elementToFocus) {
            elementToFocus.focus();
          }
        }
        
        if (announcementMessage) {
          setTimeout(() => {
            FacetFiltersForm.announceFilterChange(announcementMessage);
          }, 200);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event, activeElementId, announcementMessage = '') {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    
    if (activeElementId) {
      const elementToFocus = document.getElementById(activeElementId);
      if (elementToFocus) {
        elementToFocus.focus();
      }
    }
    
    if (announcementMessage) {
      setTimeout(() => {
        FacetFiltersForm.announceFilterChange(announcementMessage);
      }, 200);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static setFocusAfterFilterChange(filterName = '') {
    // Announce immediately before setting focus
    const activeFacets = document.querySelector('.active-facets-desktop') || document.querySelector('.active-facets-mobile');
    
    if (activeFacets && activeFacets.offsetParent !== null) {
      // If active-facets element exists and is visible, announce and set focus
      const message = filterName 
        ? `${filterName} Filter removed. Results have been refreshed.`
        : 'Filters all removed. Results have been refreshed.';
      console.log('Announcing:', message); // Debug log
      FacetFiltersForm.announceFilterChange(message);
      
      // Then set focus with a delay to ensure announcement is processed
      setTimeout(() => {
        activeFacets.setAttribute('tabindex', '-1');
        activeFacets.focus();
      }, 150);
    } else {
      // If no active filters, announce all cleared and focus on product grid
      console.log('Announcing: All filters have been cleared'); // Debug log
      FacetFiltersForm.announceFilterChange('All filters have been cleared. Results have been refreshed.');
      
      setTimeout(() => {
        const productGrid = document.getElementById('ProductGridContainer');
        const productCount = document.getElementById('ProductCount') || document.getElementById('ProductCountDesktop');
        
        if (productCount) {
          productCount.setAttribute('tabindex', '-1');
          productCount.focus();
        } else if (productGrid) {
          productGrid.setAttribute('tabindex', '-1');
          productGrid.focus();
        }
      }, 150);
    }
  }

  static announceFilterChange(message) {
    // Remove any existing live region to force fresh announcement
    const existingRegion = document.getElementById('filter-announcement-live-region');
    if (existingRegion) {
      existingRegion.remove();
    }
    
    // Create a fresh aria-live region each time
    const liveRegion = document.createElement('div');
    liveRegion.id = 'filter-announcement-live-region';
    liveRegion.setAttribute('aria-live', 'assertive');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    liveRegion.className = 'visually-hidden';
    liveRegion.textContent = message;
    document.body.appendChild(liveRegion);
    
    // Remove the live region after announcement to clean up
    setTimeout(() => {
      liveRegion.remove();
    }, 1000);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event, announcementMessage = '') {
    FacetFiltersForm.renderPage(searchParams, event, true, false, announcementMessage);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    let announcementMessage = '';
    if (event.target && event.target.tagName === 'INPUT' && event.target.type === 'checkbox') {
      const label = event.target.closest('label');
      if (label) {
        const textSpan = label.querySelector('.visually-hidden');
        let filterName = event.target.value;
        if (textSpan) {
          filterName = textSpan.textContent.replace(/\s+/g, ' ').trim();
        }
        const stateStr = event.target.checked ? 'checked' : 'not checked';
        announcementMessage = `${filterName} has been selected`;
      }
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event, announcementMessage);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event, announcementMessage);
    }
  }

  onActiveFilterClick(event, buttonElement = null) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    
    // Use the passed button element, or fall back to event.currentTarget
    const filterButton = buttonElement || event.currentTarget;
    
    // Extract filter name from the clicked remove button
    let filterName = '';
    const buttonInner = filterButton.querySelector('.active-facets__button-inner');
    
    if (buttonInner) {
      // Get the text content and remove SVG content and visually-hidden text
      const textNodes = Array.from(buttonInner.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim())
        .map(node => node.textContent.trim())
        .join(' ');
      
      filterName = textNodes || '';
    }
    
    const rawUrl = filterButton.dataset.url || filterButton.href || '';
    const url = rawUrl.indexOf('?') == -1 ? '' : rawUrl.slice(rawUrl.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, null, true, true, filterName);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetControl = this.querySelector('button, a');
    if (!facetControl) return;

    if (facetControl.tagName.toLowerCase() === 'a') {
      facetControl.setAttribute('role', 'button');
    }

    facetControl.addEventListener('click', this.closeFilter.bind(this));

    facetControl.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.closeFilter(event);
      }
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.closeFilter(event);
      }
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event, this.querySelector('button, a'));
  }
}

customElements.define('facet-remove', FacetRemove);
